/* eslint-disable i18next/no-literal-string */
import {Badge} from '@dropbox/dig-components/badge';
import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Typeahead} from '@dropbox/dig-components/typeahead';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {RepeatLine, SearchLine} from '@dropbox/dig-icons/assets';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  AdminRoadmapJiraGroupMappingsService,
  AdminRoadmapJiraGroupMappingStatus,
  type AdminRoadmapJiraGroupOption,
  type AdminRoadmapJiraGroupTeamOption,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Column, Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser, isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

const mappingsQueryKey = ['admin', 'roadmap', 'jira-group-mappings'] as const;

interface ConsolidatedJiraGroup {
  key: string;
  label: string;
  sourceGroups: AdminRoadmapJiraGroupOption[];
  group: AdminRoadmapJiraGroupOption;
  hasConflictingMappings: boolean;
  isPartiallyMapped: boolean;
  isFullyMapped: boolean;
}

const columns: Column<ConsolidatedJiraGroup>[] = [
  {
    type: 'Jira group',
    minWidth: 220,
    sort: (a, b) => a.label.localeCompare(b.label),
  },
  {
    type: 'Issues',
    fixed: true,
    minWidth: 80,
    sort: (a, b) => a.group.issue_count - b.group.issue_count,
  },
  {
    type: 'Status',
    fixed: true,
    minWidth: 150,
    sort: (a, b) => a.group.status.localeCompare(b.group.status),
  },
  {
    type: 'Dropbox Team',
    minWidth: 360,
  },
];

const formatTimestamp = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const normalizedGroupLabel = (group: AdminRoadmapJiraGroupOption) => {
  const label = group.jira_option_label?.trim() || group.observed_labels?.[0]?.trim();
  return label || null;
};

const groupLabelKey = (label: string) => label.replace(/\s+/g, ' ').toLocaleLowerCase();

const consolidateJiraGroups = (groups: AdminRoadmapJiraGroupOption[]): ConsolidatedJiraGroup[] => {
  const groupsByLabel = new Map<string, AdminRoadmapJiraGroupOption[]>();

  groups.forEach((group) => {
    const label = normalizedGroupLabel(group);
    // Groups without a usable label cannot safely be assumed to be the same
    // option across two independently configured Jira fields.
    const key = label
      ? `label:${groupLabelKey(label)}`
      : `option:${group.jira_field_id}:${group.jira_option_id}`;
    groupsByLabel.set(key, [...(groupsByLabel.get(key) ?? []), group]);
  });

  return Array.from(groupsByLabel, ([key, sourceGroups]) => {
    const firstGroup = sourceGroups[0];
    const label = normalizedGroupLabel(firstGroup) ?? 'Unknown label';
    const mappedGroups = sourceGroups.filter((group) => group.mapping);
    const mappedTeamIds = new Set(mappedGroups.map((group) => group.mapping!.team_id));
    const mappedTeamId = mappedTeamIds.size === 1 ? mappedGroups[0].mapping!.team_id : null;
    const hasConflictingMappings = mappedTeamIds.size > 1;
    const mappingsAreAligned =
      mappedTeamId !== null &&
      sourceGroups.every((group) => group.mapping?.team_id === mappedTeamId);
    const isFullyMapped =
      mappingsAreAligned &&
      sourceGroups.every(
        (group) => group.status === AdminRoadmapJiraGroupMappingStatus.EXPLICITLY_MAPPED
      );
    const isPartiallyMapped =
      mappedTeamId !== null && !mappingsAreAligned && !hasConflictingMappings;
    const mappedGroup = mappedTeamId
      ? mappedGroups.find((group) => group.mapping?.team_id === mappedTeamId)
      : undefined;
    const candidatesById = new Map<string, AdminRoadmapJiraGroupTeamOption>();
    sourceGroups.forEach((group) => {
      group.name_match_candidates?.forEach((team) => candidatesById.set(team.team_id, team));
    });
    const nameMatchCandidates = Array.from(candidatesById.values());

    let status = AdminRoadmapJiraGroupMappingStatus.UNMAPPED;
    if (hasConflictingMappings || isPartiallyMapped) {
      status = AdminRoadmapJiraGroupMappingStatus.INVALID_TEAM;
    } else if (isFullyMapped) {
      status = AdminRoadmapJiraGroupMappingStatus.EXPLICITLY_MAPPED;
    } else if (
      sourceGroups.some((group) => group.status === AdminRoadmapJiraGroupMappingStatus.INVALID_TEAM)
    ) {
      status = AdminRoadmapJiraGroupMappingStatus.INVALID_TEAM;
    } else if (nameMatchCandidates.length === 1) {
      status = AdminRoadmapJiraGroupMappingStatus.SUGGESTED_NAME_MATCH;
    } else if (
      nameMatchCandidates.length > 1 ||
      sourceGroups.some(
        (group) => group.status === AdminRoadmapJiraGroupMappingStatus.AMBIGUOUS_NAME_MATCH
      )
    ) {
      status = AdminRoadmapJiraGroupMappingStatus.AMBIGUOUS_NAME_MATCH;
    }

    return {
      key,
      label,
      sourceGroups,
      hasConflictingMappings,
      isPartiallyMapped,
      isFullyMapped,
      group: {
        ...firstGroup,
        jira_option_label: label,
        observed_labels: Array.from(
          new Set(sourceGroups.flatMap((group) => group.observed_labels ?? []))
        ),
        issue_count: sourceGroups.reduce((total, group) => total + group.issue_count, 0),
        is_present_in_latest_sync: sourceGroups.some((group) => group.is_present_in_latest_sync),
        status,
        mapping: mappedTeamId ? mappedGroup?.mapping : null,
        mapped_team:
          mappedTeamId === null
            ? null
            : (sourceGroups.find((group) => group.mapped_team?.team_id === mappedTeamId)
                ?.mapped_team ?? null),
        name_match_candidates: nameMatchCandidates,
      },
    };
  });
};

const MappingStatus = ({row}: {row: ConsolidatedJiraGroup}) => {
  const {group} = row;
  let badge;
  if (row.hasConflictingMappings) {
    badge = <Badge tone="alert">Conflicting mappings</Badge>;
  } else if (row.isPartiallyMapped) {
    badge = <Badge tone="warning">Partially mapped</Badge>;
  } else {
    switch (group.status) {
      case AdminRoadmapJiraGroupMappingStatus.EXPLICITLY_MAPPED:
        badge = <Badge tone="success">Mapped</Badge>;
        break;
      case AdminRoadmapJiraGroupMappingStatus.SUGGESTED_NAME_MATCH:
        badge = <Badge>Suggested match</Badge>;
        break;
      case AdminRoadmapJiraGroupMappingStatus.AMBIGUOUS_NAME_MATCH:
        badge = <Badge tone="warning">Multiple matches</Badge>;
        break;
      case AdminRoadmapJiraGroupMappingStatus.INVALID_TEAM:
        badge = <Badge tone="alert">Needs attention</Badge>;
        break;
      default:
        badge = <Badge>Unmapped</Badge>;
    }
  }

  return (
    <Stack gap="4">
      <Box>{badge}</Box>
      {!group.is_present_in_latest_sync ? (
        <Text size="small" color="subtle">
          Not in latest sync
        </Text>
      ) : null}
    </Stack>
  );
};

const choicesForGroups = (
  groups: AdminRoadmapJiraGroupOption[],
  activeTeams: AdminRoadmapJiraGroupTeamOption[]
) => {
  const choices = [...activeTeams];
  groups.forEach((group) => {
    if (group.mapped_team && !choices.some((team) => team.team_id === group.mapped_team?.team_id)) {
      choices.unshift(group.mapped_team);
    }
  });
  return choices;
};

const UNMAPPED_TEAM_VALUE = '__unmapped__';
const CONFLICTING_MAPPINGS_VALUE = '__conflicting_mappings__';

interface MappingTeamChoice {
  value: string;
  title: string;
  subtitle: string;
  disabled: boolean;
  suggested: boolean;
  warning: string | null;
}

const inputLabelForChoice = (choice?: MappingTeamChoice) => {
  if (!choice) return 'Unmapped';
  if (choice.value === UNMAPPED_TEAM_VALUE) return choice.title;
  if (choice.value === CONFLICTING_MAPPINGS_VALUE) return choice.title;
  if (choice.warning === 'Unavailable') return `${choice.title} (${choice.subtitle})`;
  return choice.subtitle || choice.title;
};

const MappingControl = ({
  row,
  teamOptions,
}: {
  row: ConsolidatedJiraGroup;
  teamOptions: AdminRoadmapJiraGroupTeamOption[];
}) => {
  const {group, sourceGroups} = row;
  const queryClient = useQueryClient();
  const isDropdownClick = useRef(false);
  const persistedTeamId = row.hasConflictingMappings
    ? CONFLICTING_MAPPINGS_VALUE
    : (group.mapping?.team_id ?? '');
  const [selection, setSelection] = useState(persistedTeamId);
  const choices = useMemo(
    () => choicesForGroups(sourceGroups, teamOptions),
    [sourceGroups, teamOptions]
  );
  const suggestedTeamIds = useMemo(
    () => new Set((group.name_match_candidates ?? []).map((team) => team.team_id)),
    [group.name_match_candidates]
  );
  const missingMappedTeamIds = useMemo(
    () =>
      Array.from(
        new Set(
          sourceGroups
            .filter((sourceGroup) => sourceGroup.mapping && !sourceGroup.mapped_team)
            .map((sourceGroup) => sourceGroup.mapping!.team_id)
        )
      ),
    [sourceGroups]
  );
  const suggestedTeam = group.name_match_candidates?.[0] ?? null;
  const typeaheadChoices = useMemo<MappingTeamChoice[]>(() => {
    const teamChoices = choices
      .map((team) => ({
        value: team.team_id,
        title: team.name,
        subtitle: team.display_path,
        disabled: team.is_inactive,
        suggested: suggestedTeamIds.has(team.team_id),
        warning: team.is_inactive
          ? 'Inactive'
          : team.has_hierarchy_error
            ? 'Hierarchy incomplete'
            : null,
      }))
      .sort(
        (left, right) =>
          Number(right.suggested) - Number(left.suggested) ||
          left.subtitle.localeCompare(right.subtitle)
      );

    return [
      {
        value: UNMAPPED_TEAM_VALUE,
        title: 'Unmapped',
        subtitle: 'Remove Dropbox Team mapping',
        disabled: false,
        suggested: false,
        warning: null,
      },
      ...(row.hasConflictingMappings
        ? [
            {
              value: CONFLICTING_MAPPINGS_VALUE,
              title: 'Multiple Dropbox Teams',
              subtitle: 'Requesting and Delivering Group mappings disagree',
              disabled: true,
              suggested: false,
              warning: 'Needs attention',
            },
          ]
        : []),
      ...missingMappedTeamIds.map((teamId) => ({
        value: teamId,
        title: 'Unavailable Team',
        subtitle: teamId,
        disabled: true,
        suggested: false,
        warning: 'Unavailable',
      })),
      ...teamChoices,
    ];
  }, [choices, missingMappedTeamIds, row.hasConflictingMappings, suggestedTeamIds]);
  const selectedChoice = typeaheadChoices.find(
    (choice) => choice.value === (selection || UNMAPPED_TEAM_VALUE)
  );
  const selectedLabel = inputLabelForChoice(selectedChoice);
  const persistedChoice = typeaheadChoices.find(
    (choice) => choice.value === (persistedTeamId || UNMAPPED_TEAM_VALUE)
  );
  const persistedLabel = inputLabelForChoice(persistedChoice);
  const [inputValue, setInputValue] = useState(persistedLabel);
  const normalizedQuery = inputValue.trim().toLocaleLowerCase();
  const filteredChoices = typeaheadChoices.filter(
    (choice) =>
      normalizedQuery.length === 0 ||
      choice.title.toLocaleLowerCase().includes(normalizedQuery) ||
      choice.subtitle.toLocaleLowerCase().includes(normalizedQuery)
  );

  const mutation = useMutation<void, Error, string>({
    mutationFn: async (teamId) => {
      const service = getService(AdminRoadmapJiraGroupMappingsService);
      if (teamId) {
        await Promise.all(
          sourceGroups
            .filter((sourceGroup) => sourceGroup.mapping?.team_id !== teamId)
            .map((sourceGroup) =>
              service.upsertJiraGroupMappingApiV1AdminRoadmapJiraGroupMappingsJiraFieldIdJiraOptionIdPut(
                sourceGroup.jira_field_id,
                sourceGroup.jira_option_id,
                {team_id: teamId}
              )
            )
        );
      } else {
        await Promise.all(
          sourceGroups
            .filter((sourceGroup) => sourceGroup.mapping)
            .map((sourceGroup) =>
              service.deleteJiraGroupMappingApiV1AdminRoadmapJiraGroupMappingsJiraFieldIdJiraOptionIdDelete(
                sourceGroup.jira_field_id,
                sourceGroup.jira_option_id
              )
            )
        );
      }
    },
    onError: () => {
      setSelection(persistedTeamId);
      setInputValue(persistedLabel);
    },
    // Separate Jira field writes are best-effort as a group. Always refetch so
    // a partial network failure immediately exposes the state that needs repair.
    onSettled: async () => {
      await queryClient.invalidateQueries({queryKey: mappingsQueryKey});
    },
  });

  useEffect(() => {
    setSelection(persistedTeamId);
    setInputValue(persistedLabel);
    mutation.reset();
    // A refreshed mapping is the source of truth for this controlled field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedLabel, persistedTeamId]);

  const handleSelection = (value: string) => {
    const teamId = value === UNMAPPED_TEAM_VALUE ? '' : value;
    const choice = typeaheadChoices.find((candidate) => candidate.value === value);
    setSelection(teamId);
    setInputValue(inputLabelForChoice(choice));
    const needsUpdate = teamId
      ? sourceGroups.some((sourceGroup) => sourceGroup.mapping?.team_id !== teamId)
      : sourceGroups.some((sourceGroup) => sourceGroup.mapping);
    if (needsUpdate) {
      mutation.mutate(teamId);
    }
  };

  const renderTeamChoice = (choice: MappingTeamChoice) => (
    <Typeahead.Row
      key={choice.value}
      value={choice.value}
      disabled={choice.disabled}
      onMouseDown={() => {
        isDropdownClick.current = true;
      }}
      withTitle={<Text isBold>{choice.title}</Text>}
      withSubtitle={choice.subtitle}
      withRightAccessory={
        choice.suggested ? (
          <Badge>Suggested</Badge>
        ) : choice.warning ? (
          <Badge tone="warning">{choice.warning}</Badge>
        ) : undefined
      }
    />
  );

  const renderNoResults = () => (
    <Typeahead.Row disabled withTitle={<Text color="subtle">No Dropbox Teams found</Text>} />
  );

  const handleInputBlur = () => {
    if (isDropdownClick.current) {
      isDropdownClick.current = false;
      return;
    }
    setInputValue(selectedLabel);
  };

  return (
    <Stack gap="4">
      <Typeahead.Wrapper onSelection={handleSelection} onBlur={handleInputBlur}>
        {({getTriggerProps, getContentProps}) => (
          <Box style={{width: '100%', maxWidth: 480}}>
            <TextInput
              id={`jira-group-team-${row.key}`}
              size="small"
              aria-label={`Dropbox Team for ${group.jira_option_label ?? group.jira_option_id}`}
              placeholder="Search Dropbox Teams"
              value={inputValue}
              disabled={mutation.isPending}
              isInvalid={group.status === AdminRoadmapJiraGroupMappingStatus.INVALID_TEAM}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              withLeftAccessory={<UIIcon src={SearchLine} />}
              style={{width: '100%'}}
              {...getTriggerProps({
                onChange: (event) => setInputValue((event.currentTarget as HTMLInputElement).value),
                onFocus: (event) => {
                  (event.currentTarget as HTMLInputElement).select();
                  setInputValue('');
                },
              })}
            />
            <Typeahead.Container {...getContentProps()} style={{width: 480, maxWidth: '90vw'}}>
              <Typeahead.Results
                results={filteredChoices.length ? filteredChoices : [{} as MappingTeamChoice]}
                renderRow={filteredChoices.length ? renderTeamChoice : renderNoResults}
                initialResults={8}
                maxResults={8}
              />
            </Typeahead.Container>
          </Box>
        )}
      </Typeahead.Wrapper>
      {mutation.isPending ? (
        <Text size="small" color="subtle" role="status">
          Saving mapping…
        </Text>
      ) : mutation.isError ? (
        <Text size="small" color="subtle" role="alert">
          Unable to save this mapping. Try again.
        </Text>
      ) : group.status === AdminRoadmapJiraGroupMappingStatus.SUGGESTED_NAME_MATCH &&
        suggestedTeam ? (
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 4}}>
          <Text size="small" color="subtle">
            Suggested:
          </Text>
          <Chip
            variant="standard"
            size="small"
            disabled={suggestedTeam.is_inactive}
            aria-label={`Use suggested Dropbox Team ${suggestedTeam.display_path}`}
            onClick={() => handleSelection(suggestedTeam.team_id)}
          >
            <Chip.Content>{suggestedTeam.display_path}</Chip.Content>
          </Chip>
        </Box>
      ) : null}
    </Stack>
  );
};

const GroupRow = ({
  row,
  teamOptions,
}: {
  row: ConsolidatedJiraGroup;
  teamOptions: AdminRoadmapJiraGroupTeamOption[];
}) => {
  const {group} = row;
  return (
    <Table.Row data-testid={`jira-group-row-${row.key}`}>
      <Table.Cell>
        <Text isBold>{row.label}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text>{group.issue_count}</Text>
      </Table.Cell>
      <Table.Cell>
        <MappingStatus row={row} />
      </Table.Cell>
      <Table.Cell>
        <MappingControl row={row} teamOptions={teamOptions} />
      </Table.Cell>
    </Table.Row>
  );
};

export const AdminRoadmapJiraGroupMappings = () => {
  const navigate = useNavigate();
  const pulseUser = useAtomValue(pulseUserAtom);
  const canView = isUserAdmin(pulseUser) || isSuperUser(pulseUser);

  useDocumentTitle('Jira group mappings');

  const {data, isError, isFetching, isLoading, refetch} = useQuery({
    queryKey: mappingsQueryKey,
    queryFn: () =>
      getService(
        AdminRoadmapJiraGroupMappingsService
      ).listJiraGroupMappingsApiV1AdminRoadmapJiraGroupMappingsGet(),
    enabled: canView,
  });

  if (!pulseUser) return null;
  if (!canView) {
    navigate('/');
    return null;
  }

  const groups = consolidateJiraGroups(data?.groups ?? []);
  const teamOptions = data?.team_options ?? [];
  const mappedCount = groups.filter((group) => group.isFullyMapped).length;
  const snapshotBuiltAt = formatTimestamp(data?.snapshot_built_at);

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {
          children: 'Jira group mappings',
          to: ROUTE_PATHS.ADMIN_ROADMAP_JIRA_GROUP_MAPPINGS,
        },
      ]}
      width="100%"
    >
      <Stack gap="24">
        <Stack gap="8">
          <Title
            size={24}
            withAccessoryEnd={
              <Tooltip title="Refresh">
                <IconButton
                  aria-label="Refresh Jira group mappings"
                  variant="opacity"
                  shape="circular"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                >
                  <UIIcon src={RepeatLine} />
                </IconButton>
              </Tooltip>
            }
          >
            Jira group mappings
          </Title>
          <Text color="subtle">
            Map groups observed in the Roadmap Jira sync to active Dropbox Teams. One mapping
            applies to matching Requesting and Delivering Group options, and Team paths show where
            each Team sits in the hierarchy.
          </Text>
          {data ? (
            <Text size="small" color="subtle">
              {mappedCount} of {groups.length} groups mapped
              {snapshotBuiltAt ? ` · Jira snapshot built ${snapshotBuiltAt}` : ''}
            </Text>
          ) : null}
        </Stack>

        {data && !data.source_available ? (
          <SystemBanner tone="warning" role="status">
            <Text>
              The latest Jira source snapshot is unavailable. Existing mappings remain editable, but
              new Jira groups will not appear until the next successful sync.
            </Text>
          </SystemBanner>
        ) : null}

        {isError ? (
          <SystemBanner tone="alert" role="alert">
            <Text>Unable to load Jira group mappings. Try refreshing the page.</Text>
          </SystemBanner>
        ) : null}

        <Table columns={columns} data={groups} compact>
          <Table.Header />
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box display="flex" alignItems="center" style={{gap: 8}}>
                    <Spinner size="small" />
                    <Text color="subtle">Loading Jira group mappings</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : isError ? null : groups.length ? (
              groups.map((group) => (
                <GroupRow key={group.key} row={group} teamOptions={teamOptions} />
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={columns.length}>
                  <Box paddingY="16">
                    <Text color="subtle">No Jira groups were found in the latest sync.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Stack>
    </Layout.Container>
  );
};
